'use client';

import { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { ExclamationTriangleIcon, ExclamationCircleIcon, CheckCircleIcon, HandThumbUpIcon } from '@heroicons/react/24/solid';

import { AlertType, AlertVariant } from '@constants/alert';

import ButtonV1 from '@components/button-v1';

export default function AlertConfirm(props) {
    const { 
        open,
        type, 
        options,
        onOkClick,
        onCancelClick,
        onClose,
    } = props; 

    const useType = type ? type : "alert";
    const okLabel = options.okLabel ? options.okLabel : "Ok";
    const cancelLabel = options.cancelLabel ? options.cancelLabel : "Cancel";

    let Icon = null;
    let iconColor = "";
    let bgColor = "bg-white";
    let textColor = "text-blue01";
    let okVariant = "alert-neutral-solid";
    let cancelVariant = "alert-neutral";
    if (options.variant) {
        switch (options.variant) {
            case AlertVariant.SUCCESS:
                Icon = CheckCircleIcon;
                iconColor = "text-green01";
                bgColor = "bg-light-green03";
                textColor = "text-dark-green01";
                okVariant = "alert-success";
                cancelVariant = "alert-success";
                break;
            case AlertVariant.WARNING:
                Icon = ExclamationTriangleIcon;
                iconColor = "text-yellow01";
                bgColor = "bg-yellow04";
                textColor = "text-dark-yellow02";
                okVariant = "alert-warning";
                cancelVariant = "alert-warning";
                break;
            case AlertVariant.ERROR:
                Icon = ExclamationCircleIcon;
                iconColor = "text-red02";
                bgColor = "bg-pink";
                textColor = "text-red02";
                okVariant = "alert-error";
                cancelVariant = "alert-error";
                break;
            case AlertVariant.INFORMATION:
                Icon = HandThumbUpIcon;
                iconColor = "text-blue01";
                bgColor = "bg-blue02";
                textColor = "text-blue01";
                okVariant = "alert-information";
                cancelVariant = "alert-information";
                break;    
            default:
                break;
        }
    }

    const onDialogOkClick = (e) => {
        e.preventDefault();
        if (onOkClick) {
            onOkClick();
        }
        if (onClose) {
            onClose();
        }
    }

    const onDialogCancelClick = (e) => {
       e.preventDefault(); 
        if (onCancelClick) {
            onCancelClick();
        }
        if (onClose) {
            onClose();
        }
    }
    
    const onDialogClose = () => {
        if (onClose) {
            onClose();
        }
    }
    
    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-99" onClose={onDialogClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-75"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-75"
                    leaveTo="opacity-0"                
                    >
                    <div className="fixed inset-0 bg-black opacity-75 transition-opacity"/>
                </TransitionChild>
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className={`relative flex items-start gap-3 transform rounded-lg p-4 text-left shadow-modal transition-all sm:my-8 max-w-alert-narrow lg:max-w-alert-wide ${bgColor}`}>
                                {Icon && (
                                    <Icon className={`size-5 ${iconColor}`}/>
                                )}
                                <div className="flex flex-col gap-3">
                                    {options.title && (
                                        <DialogTitle
                                            as="h2"
                                            className={`font-vg-regular text-base leading-120 ${textColor}`}
                                        >
                                            {options.title}
                                        </DialogTitle>
                                    )}
                                    {options.text && (
                                        <p 
                                            className={`font-vg-book text-sm leading-150 ${textColor}`}
                                            dangerouslySetInnerHTML={{__html: options.text}}
                                        />
                                    )}
                                    <div className="flex gap-3">
                                        {useType === AlertType.CONFIRM && (
                                            <ButtonV1 
                                                variant={cancelVariant} 
                                                size="x-small" 
                                                label={cancelLabel}
                                                onClick={onDialogCancelClick}
                                            />
                                        )}
                                        <ButtonV1 
                                            variant={okVariant} 
                                            size="x-small" 
                                            label={okLabel}
                                            onClick={onDialogOkClick}
                                        />
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
} 